/**
 * Dates. Business time zone is Asia/Dubai. Date-only fields ("2026-10-07") are
 * calendar dates and must never be parsed through `new Date(string)`, which
 * shifts them by the browser's offset.
 */
export const BUSINESS_TZ = 'Asia/Dubai';

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parses YYYY-MM-DD into a UTC-midnight Date for formatting only. */
export function parseDateOnly(value: string): Date {
  const m = DATE_ONLY_RE.exec(value);
  if (!m) throw new Error(`parseDateOnly: expected YYYY-MM-DD, got ${value}`);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

export function parseInstant(value: string): Date {
  const d = new Date(Date.parse(value));
  if (Number.isNaN(d.getTime())) throw new Error(`parseInstant: invalid ISO date-time ${value}`);
  return d;
}

/** Today's calendar date in Dubai as YYYY-MM-DD. */
export function todayInDubai(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return parts; // en-CA gives YYYY-MM-DD
}

/** Current reporting period YYYY-MM in Dubai. */
export function currentPeriod(now: Date = new Date()): string {
  return todayInDubai(now).slice(0, 7);
}

export function shiftPeriod(period: string, months: number): string {
  const [y, m] = period.split('-').map((p) => Math.trunc(Number(p))) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + months, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function addDaysToDateOnly(value: string, days: number): string {
  const d = parseDateOnly(value);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function isDateOnly(value: string): boolean {
  return DATE_ONLY_RE.test(value);
}
